<script setup>
/* global axios */
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';

const route = useRoute();
const store = useStore();
const accountId = computed(() => Number(route.params.accountId));
const inboxes = computed(() => store.getters['inboxes/getInboxes'] || []);
const startsAt = ref('');
const endsAt = ref('');
const selectedInboxIds = ref([]);
const loading = ref(false);
const result = ref(null);
const estimate = ref(null);
const validating = ref(false);
const money = value => Number(value).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const number = value => Number(value).toLocaleString('pt-BR');
const recipientEmail = ref(store.getters.getCurrentUser?.email || '');
const scopeKey = computed(() => JSON.stringify([accountId.value, startsAt.value, endsAt.value, [...selectedInboxIds.value].sort(), recipientEmail.value.trim()]));
watch(scopeKey, () => { estimate.value = null; result.value = null; });
const generatedAt = ref('');
const printReport = () => window.print();
const formatDate = value => new Date(value).toLocaleString('pt-BR');

const canRun = computed(
  () => startsAt.value && endsAt.value && selectedInboxIds.value.length > 0 && /^[^\s@,;<>]+@[^\s@,;<>]+\.[^\s@,;<>]+$/.test(recipientEmail.value.trim())
);

const requestScope = () => ({
  starts_at: new Date(startsAt.value).toISOString(),
  ends_at: new Date(endsAt.value).toISOString(),
  inbox_ids: [...selectedInboxIds.value],
  recipient_email: recipientEmail.value.trim(),
});
const validateExecution = async () => {
  if (!canRun.value || loading.value || validating.value) return;
  estimate.value = null;
  result.value = null;
  validating.value = true;
  const key = scopeKey.value;
  try {
    const { data } = await axios.post(
      `/api/v1/accounts/${accountId.value}/most_insights`,
      { ...requestScope(), validate_only: true }
    );
    if (scopeKey.value === key && data.validated) estimate.value = data;
  } catch (error) {
    useAlert(error?.response?.data?.error || 'MOST Insights: não foi possível validar a execução.');
  } finally {
    validating.value = false;
  }
};

const runInsights = async () => {
  if (!canRun.value || loading.value || validating.value || !estimate.value) return;
  if (Date.now() >= Date.parse(estimate.value.expires_at)) { estimate.value = null; useAlert('Validação expirada. Clique em Validar execução novamente.'); return; }
  loading.value = true;
  result.value = null;
  try {
    const { data } = await axios.post(
      `/api/v1/accounts/${accountId.value}/most_insights`,
      {
        starts_at: new Date(startsAt.value).toISOString(),
        ends_at: new Date(endsAt.value).toISOString(),
        inbox_ids: selectedInboxIds.value,
        recipient_email: recipientEmail.value.trim(),
        validation_token: estimate.value.validation_token,
      }
    );
    result.value = data;
    estimate.value = null;
    generatedAt.value = new Date().toLocaleString('pt-BR');
  } catch (error) {
    estimate.value = null;
    useAlert(
      error?.response?.data?.error ||
        error?.message ||
        'MOST Insights: não foi possível gerar a análise.'
    );
  } finally {
    loading.value = false;
  }
};

onMounted(() => { store.dispatch('inboxes/get'); if (!recipientEmail.value) recipientEmail.value = store.getters.getCurrentUser?.email || ''; });
</script>

<template>
  <div class="most-insights-page flex flex-col w-full h-full overflow-auto p-6 gap-6">
    <div>
      <h1 class="text-2xl font-semibold text-n-slate-12">MOST Insights</h1>
      <p class="mt-1 text-sm text-n-slate-10">
        Relatório executivo por e-mail. Selecione até 90 dias e as caixas; até 90.000 mensagens serão processadas em lotes.
      </p>
    </div>

    <div class="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl">
      <label class="flex flex-col gap-2 text-sm text-n-slate-11">
        Início do período *
        <input v-model="startsAt" type="datetime-local" class="h-10 rounded-md border border-n-weak px-3 bg-n-alpha-1" />
      </label>
      <label class="flex flex-col gap-2 text-sm text-n-slate-11">
        Fim do período *
        <input v-model="endsAt" type="datetime-local" class="h-10 rounded-md border border-n-weak px-3 bg-n-alpha-1" />
      </label>
    </div>

    <div class="max-w-4xl">
      <h2 class="text-sm font-medium text-n-slate-12 mb-2">Caixas postais a analisar *</h2>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-2 border border-n-weak rounded-lg p-4">
        <label v-for="inbox in inboxes" :key="inbox.id" class="flex items-center gap-2 text-sm text-n-slate-11">
          <input v-model="selectedInboxIds" type="checkbox" :value="Number(inbox.id)" />
          <span>{{ inbox.name }}</span>
        </label>
      </div>
      <p v-if="!selectedInboxIds.length" class="mt-2 text-xs text-n-slate-9">Selecione pelo menos uma caixa.</p>
    </div>

    <label class="flex flex-col gap-2 text-sm text-n-slate-11 max-w-4xl">
      E-mail para receber o relatório *
      <input v-model="recipientEmail" type="email" class="h-10 rounded-md border border-n-weak px-3 bg-n-alpha-1" />
    </label>
    <div class="flex gap-3">
      <button type="button" class="px-4 py-2 rounded-md bg-n-brand text-white disabled:opacity-50" :disabled="!canRun || loading || validating" @click="validateExecution">
        {{ validating ? 'Validando...' : 'Validar execução' }}
      </button>
      <button type="button" class="px-4 py-2 rounded-md bg-n-brand text-white disabled:opacity-50" :disabled="!canRun || !estimate || loading || validating" @click="runInsights">
        {{ loading ? 'Executando...' : 'Executar' }}
      </button>
    </div>
    <section v-if="estimate" class="max-w-4xl border border-n-weak rounded-lg p-5 space-y-3 text-sm">
      <h2 class="text-lg font-semibold text-n-slate-12">Custo aproximado do relatório — OpenAI</h2>
      <p>{{ number(estimate.messages) }} mensagens · {{ number(estimate.characters) }} caracteres de texto · {{ number(estimate.conversations) }} conversas</p>
      <table class="w-full text-left">
        <thead><tr><th class="py-2">Processamento</th><th>Tokens estimados</th><th>R$ / milhão de tokens</th><th>Custo estimado</th></tr></thead>
        <tbody>
          <tr><td class="py-2">Entrada, incluindo consolidação</td><td>{{ number(estimate.input_tokens) }}</td><td>{{ money(estimate.input_price_brl_per_million) }}</td><td>{{ money(estimate.input_cost_brl) }}</td></tr>
          <tr><td class="py-2">Saída dos lotes e relatório</td><td>{{ number(estimate.output_tokens) }}</td><td>{{ money(estimate.output_price_brl_per_million) }}</td><td>{{ money(estimate.output_cost_brl) }}</td></tr>
        </tbody>
      </table>
      <p class="text-lg font-semibold">Total aproximado: {{ money(estimate.total_cost_brl) }}</p>
      <p>Com saídas maiores, a referência sobe para {{ money(estimate.higher_output_total_brl) }}. Não é um limite de cobrança.</p>
      <p>Modelo: {{ estimate.model }} · US$ 1 = {{ money(estimate.exchange_rate) }} · {{ number(estimate.batches) }} lotes e {{ number(estimate.consolidation_calls) }} chamadas de consolidação.</p>
      <p class="text-xs text-n-slate-10">{{ estimate.exchange_description }} {{ estimate.assumptions }}</p>
      <p class="text-xs text-n-slate-10">Validar não consome tokens da OpenAI. Validação válida por 15 minutos; alterações nos filtros ou nos dados exigem nova validação.</p>
      <a :href="estimate.pricing_source" target="_blank" rel="noopener noreferrer" class="underline text-xs">Tarifas da OpenAI · verificadas em {{ estimate.pricing_checked_at }}</a>
    </section>

    <section v-if="result" id="most-insights-report" class="max-w-5xl border border-n-weak rounded-lg p-5 space-y-4">
      <div>
        <h2 class="text-lg font-semibold text-n-slate-12">{{ result.accepted ? 'Solicitação recebida' : 'MOST Insights — Relatório Executivo' }}</h2>
        <p class="text-xs text-n-slate-9">Gerado em {{ generatedAt }}</p>
        <button v-if="!result.accepted" class="most-print-button px-4 py-2 rounded-md bg-n-brand text-white mt-3" type="button" @click="printReport">Imprimir / Salvar PDF</button>
        <p class="text-xs text-n-slate-9 mt-1">
          {{ formatDate(result.period.starts_at) }} até {{ formatDate(result.period.ends_at) }} ·
          {{ result.inboxes.map(item => item.name).join(', ') }}
        </p>
        <p class="text-xs text-n-slate-9">
          {{ result.totals.conversations }} conversas · {{ result.totals.messages }} mensagens
          <span v-if="result.totals.truncated"> · processamento limitado a {{ result.totals.messages_sent_to_ai }} mensagens</span>
        </p>
      </div>
      <p v-if="result.accepted" class="text-sm">Solicitação {{ result.job_id }}. Você pode sair desta tela; o processamento continuará em segundo plano.</p>
      <div class="whitespace-pre-wrap text-sm text-n-slate-11">{{ result.executive_summary }}</div>
      <div v-if="Object.keys(result.analysis || {}).length" class="space-y-4">
        <div v-for="(value, key) in result.analysis" :key="key">
          <h3 class="font-semibold text-n-slate-12">{{ String(key).replace(/_/g, ' ') }}</h3>
          <p v-if="typeof value === 'string'" class="whitespace-pre-wrap text-sm">{{ value }}</p>
          <ul v-else-if="Array.isArray(value)" class="list-disc pl-5 text-sm"><li v-for="(item, i) in value" :key="i">{{ typeof item === 'string' ? item : JSON.stringify(item) }}</li></ul>
          <pre v-else class="whitespace-pre-wrap text-sm">{{ JSON.stringify(value, null, 2) }}</pre>
        </div>
      </div>
    </section>
  </div>
</template>

<style>
@media print {
  @page { size: A4; margin: 18mm; @bottom-right { content: "Página " counter(page); } }
  body * { visibility: hidden; }
  #most-insights-report, #most-insights-report * { visibility: visible; }
  #most-insights-report { position: absolute; left: 0; top: 0; width: 100%; max-width: none; border: 0; padding: 0; color: #111; background: white; }
  #most-insights-report * { color: #111; overflow: visible; }
  .most-print-button { display: none; }
  .most-insights-page { overflow: visible; height: auto; }
  #most-insights-report h2, #most-insights-report h3 { break-after: avoid; }
  #most-insights-report pre { white-space: pre-wrap; overflow-wrap: anywhere; }
}
</style>
